import type { ProjectTypeId } from "@features/Login/types";
import type { TChubbBillInsuranceStatus } from "@services/insurance/types";
import type { PlanId } from "@services/jointBillServiceProviderBillPlans/types";
import { type TCurrency } from "@/types";

export interface IOverview {
  firstName: string;
  lastName: string;
  jointBillServices: IOverviewJointBillService[];
}

export interface IOverviewOtherUserData {
  firstName: string;
  lastName: string;
  isOwner: boolean;
}

export interface IOverviewJointBillService {
  jointBillServiceId: number;
  displayName?: string;
  sortOrder: number;
  address: string;
  isOwner: boolean;
  projectTypeId?: ProjectTypeId;
  previousPeriod: IOverviewPeriod;
  currentPeriod: IOverviewPeriod;
  otherUsers?: IOverviewOtherUserData[];
}

export interface IOverviewPeriod {
  year: number;
  month: number;
  periodStatus: PeriodState;
  billTotal: number;
  paymentTotal: number;
  paymentDate: string;
  displayCustomerHasToPayWarning?: boolean;
}

export interface IJointBillServicePeriodDataRequestParams {
  jointBillServiceId: IJointBillServicePeriodData["jointBillServiceId"];
  periodId: number;
}

export interface IDonationData {
  additionalInformationUrl: string;
  canBeSelected: boolean;
  description: string;
  donationServiceProviderId: number;
  isSelected: boolean;
  expectedPaymentAmount: number;
  totalAmount: number;
  isPaid: boolean;
}

export enum SelectionState {
  NOT_SELECTED = "notSelected",
  PARTIALLY_SELECTED = "partiallySelected",
  SELECTED = "selected",
}

export enum RequestedSpBillStateId {
  WAITING = "waiting",
  CONFIRMED = "confirmed",
  CANCELLED = "cancelled",
  DELETED = "deleted",
}

export interface IRequestedSpBillData {
  requestedServiceProviderBillFileExist: boolean;
  requestedServiceProviderBillId: number;
  requestedServiceProviderBillStateId: RequestedSpBillStateId;
  serviceProviderNameIsKnown: boolean;
  serviceProviderName: string;
}

export interface IJointBillServicePeriodData {
  jointBillServiceId: number;
  periodId: number;
  billData: IBillData[];
  donationData: IDonationData[];
  jointBillServicePeriodState: PeriodState;
  totalExpectedPayment?: number;
  totalAmount?: number;
  totalSum?: number;
  paidAmount?: number;
  unpaidAmount?: number;
  projectBillPdfDownloadable: boolean;
  isPdfFileDownloadAvailable: boolean;
  canOpenPeriod?: boolean;
  selectionState: SelectionState;
  selectionStateCanBeChanged?: boolean;
  canBePaid?: boolean;
  jointBillServiceProviderBillPlanId: PlanId;
  requestedServiceProviderBillData: IRequestedSpBillData[];
  insuranceStatus: TChubbBillInsuranceStatus;
  displayServiceProviderSuggestions: boolean;
  vouchers: IJointBillServicePeriodVoucher[];
}

export interface IBillCertificate {
  jointBillServiceCertificateId: number;
  serviceName: string;
  startDate: Date;
  endDate: Date;
}

export interface IBillData {
  billId: number;
  jointBillServiceAgreementId: number;
  customerCode: string;
  displayName: string;
  serviceProviderName: string;
  serviceProviderGroupId: string;
  billDetailizationUrl: string;
  billBookletFileUrl: string;
  totalSum: number;
  totalAmount: number;
  sortOrder: number;
  totalExpectedPaymentAmount: number;
  totalPaid: number;
  currencyId: TCurrency;
  expectedPaymentIsEditable: boolean;
  billNumber: string;
  billNumberIsEditable: boolean;
  mustEnterBillNumber?: boolean;
  isJointBillServiceProvider: boolean;
  isSelected: boolean;
  selectionStatusCanBeChanged: boolean;
  billStateId: BillState;
  isAgreementUnregistering: boolean;
  canHaveDueDate: boolean;
  dueDate: string;
  ibanNumber?: string;
  canIbanNumberBeEdited: boolean;
  billDetails: IBillDetail[];
  hasBillFile: boolean;
  canServiceProviderBeRemoved: boolean;
  canEditServiceProviderDisplayName: boolean;
  canEditServiceProviderCustomerCode: boolean;
  canTransferPreviousExpectedPayment: boolean;
  serviceProviderId: number;
  additionalServiceFileExists: boolean;
  paymentInstruction?: {
    fullName: string;
    paymentCode: string;
    paymentPurpose: string;
    personalCode: string;
    paymentCodeDescription: string;
    roikCode?: string;
  };
  certificate?: IBillCertificate;
  isTransferable?: boolean;
}

export interface IBillDetail {
  billId: number;
  billDetailId: number;
  name: string;
  nameExtended: string;
  factoryNumber: string;
  sortOrder: number;
  valueFrom: number;
  valueFromIsEditable: boolean;
  valueTo: number;
  valueToIsEditable: boolean;
  quantity: number;
  quantityIsEditable: boolean;
  tariff?: number;
  tariffIsEditable: boolean;
  decimalPlaces: number;
  measurementUnit: string;
  totalSum: number;
  totalAmount: number;
  expectedPaymentAmount: number;
  expectedPaymentAmountIsEditable: boolean;
  isNegativeAllowed: boolean;
  message?: string;
  paidDetailSum: number;
  optionalDeclaration: boolean;
  optionalDeclarationEditable: boolean;
  isMeterReading: boolean;
  meterReadingTypeId: string;
}

export enum BillState {
  Waiting = "waiting",
  Ready = "ready",
  Paid = "paid",
  Unpaid = "unpaid",
  PartiallyPaid = "partiallyPaid",
  MustBeFilledByUser = "mustBeFilledByUser",
}

export enum PeriodState {
  Waiting = "waiting",
  Ready = "ready",
  Paid = "paid",
  Unpaid = "unpaid",
  PartiallyPaid = "partiallyPaid",
  PartiallyPaidReady = "partiallyPaidReady",
  NoProviders = "noProviders",
}

export interface IJointBillServicePeriodVoucher {
  voucherId: number;
  displayName?: string;
  /**
   * Discount granted for the active payment
   */
  grantedDiscount: number;
  /**
   * Total amount of the voucher
   */
  totalAmount: number;
  usages: IJointBillServicePeriodVoucherUsage[];
}

export interface IJointBillServicePeriodVoucherUsage {
  dateUsed: string;
  usedAmount: number;
}
